// iter-116d — Structured template builder for SAs.
//
// Replaces the cold-start "type a giant paragraph" UX with a quick form:
//   • Pick intent (marketing/sales/finance/ops)
//   • Pick applicable data sources (meta/ga4/google_ads/excel/sql/...)
//   • Add KPI rows (name, formula, agg, filter)
//   • Add filter dimensions
//   • Add chart configs
//   • Click Generate → produces a clean, copy-ready Builder prompt + sample-data
//     preview before the LLM call. SA can edit the prompt then send.
//
// Prompt templates (predefined) let SA fill in placeholders quickly instead of
// crafting prompts from scratch (Gap 10 recommendation).

import React, { useState, useEffect } from "react";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Plus, Trash2, Wand2, Copy, ChevronRight, Hash, Filter, BarChart3,
    Target, Sparkles, Eye, Loader2, Lightbulb,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";


// Predefined prompt templates — SA picks one + tweaks placeholders.
// Each generates a clean structured prompt the Builder LLM converts into a
// canonical Gallery prebuilt.
const PROMPT_TEMPLATES = [
    {
        id: "marketing_performance",
        title: "Marketing Performance",
        intent: "marketing",
        sources: ["meta", "ga4", "google_ads"],
        description: "Spend, impressions, clicks, conversions, ROAS rolled up across paid channels.",
        kpis: [
            { name: "Total Spend",     formula: "SUM(spend)",           agg: "sum",   filter_col: "channel" },
            { name: "Conversions",     formula: "SUM(conversions)",     agg: "sum",   filter_col: "channel" },
            { name: "ROAS",            formula: "SUM(revenue)/SUM(spend)", agg: "ratio", filter_col: "channel" },
            { name: "CTR",             formula: "SUM(clicks)/SUM(impressions)", agg: "ratio", filter_col: "channel" },
        ],
        charts: [
            { type: "trend", title: "Spend over time", x: "date", y: "spend" },
            { type: "bar",   title: "Top campaigns by ROAS", x: "campaign_name", y: "roas", limit: 10 },
            { type: "pie",   title: "Spend by channel", x: "channel", y: "spend" },
        ],
        filters: ["date", "source_type", "channel", "country", "campaign_name"],
    },
    {
        id: "channel_mix",
        title: "Channel Mix Analysis",
        intent: "marketing",
        sources: ["ga4", "meta", "google_ads"],
        description: "Cross-channel attribution + acquisition funnel.",
        kpis: [
            { name: "Sessions",       formula: "SUM(sessions)",        agg: "sum",   filter_col: "channel" },
            { name: "Users",          formula: "SUM(users)",           agg: "sum",   filter_col: "channel" },
            { name: "Conversion Rate", formula: "SUM(conversions)/SUM(sessions)", agg: "ratio", filter_col: "channel" },
            { name: "Avg Session Duration", formula: "AVG(avg_session_duration)", agg: "avg", filter_col: "channel" },
        ],
        charts: [
            { type: "trend", title: "Sessions by channel", x: "date", y: "sessions", split: "channel" },
            { type: "bar",   title: "Top sources by conversions", x: "source", y: "conversions", limit: 8 },
        ],
        filters: ["date", "channel", "medium", "device", "country"],
    },
    {
        id: "creative_performance",
        title: "Creative Performance",
        intent: "marketing",
        sources: ["meta", "google_ads"],
        description: "Which ads + creatives perform best by CTR, conversions, ROAS.",
        kpis: [
            { name: "Top CTR",          formula: "MAX(ctr)",      agg: "max", filter_col: "ad_name" },
            { name: "Total Impressions", formula: "SUM(impressions)", agg: "sum", filter_col: "ad_name" },
            { name: "Total Conversions", formula: "SUM(conversions)", agg: "sum", filter_col: "ad_name" },
            { name: "Avg ROAS",         formula: "AVG(roas)",     agg: "avg", filter_col: "ad_name" },
        ],
        charts: [
            { type: "table", title: "Top 20 creatives", columns: ["ad_name", "headline", "impressions", "clicks", "ctr", "conversions", "roas"] },
            { type: "bar",   title: "ROAS by format", x: "format", y: "roas" },
        ],
        filters: ["date", "campaign_name", "ad_group_name", "format", "device"],
    },
    {
        id: "sales_pipeline",
        title: "Sales Pipeline",
        intent: "sales",
        sources: ["shopify", "stripe", "salesforce"],
        description: "Deal stages, conversion rates, revenue trajectory.",
        kpis: [
            { name: "Total Revenue", formula: "SUM(amount)", agg: "sum", filter_col: "stage" },
            { name: "Avg Deal Size", formula: "AVG(amount)", agg: "avg", filter_col: "stage" },
            { name: "Win Rate",      formula: "COUNT(status='won')/COUNT(*)", agg: "ratio", filter_col: "stage" },
            { name: "Active Deals",  formula: "COUNT(status='pending')", agg: "count", filter_col: "stage" },
        ],
        charts: [
            { type: "funnel", title: "Pipeline funnel", stages: ["lead", "qualified", "proposal", "negotiation", "won"] },
            { type: "trend",  title: "Revenue trend", x: "date", y: "amount" },
        ],
        filters: ["date", "stage", "source_type", "customer_id"],
    },
];


const INTENTS = [
    { value: "marketing", label: "Marketing", icon: Target },
    { value: "sales",     label: "Sales",     icon: BarChart3 },
    { value: "finance",   label: "Finance",   icon: Hash },
    { value: "ops",       label: "Operations", icon: Filter },
];


const SOURCES = [
    { value: "meta",        label: "Meta Ads" },
    { value: "ga4",         label: "Google Analytics 4" },
    { value: "google_ads",  label: "Google Ads" },
    { value: "stripe",      label: "Stripe" },
    { value: "shopify",     label: "Shopify" },
    { value: "excel",       label: "Excel / CSV" },
    { value: "sqlserver",   label: "SQL Server" },
    { value: "snowflake",   label: "Snowflake" },
];


function buildPrompt(state) {
    const lines = [];
    lines.push(`Design a ${state.intent} dashboard titled "${state.title || "Untitled"}"`);
    lines.push(`bound to the canonical model.${state.intent}.`);
    if (state.sources.length > 0) {
        lines.push(`Applicable sources: ${state.sources.join(", ")}.`);
    }
    if (state.description) {
        lines.push(`Purpose: ${state.description}`);
    }
    if (state.kpis.length > 0) {
        lines.push("");
        lines.push("KPI Tiles (one per row):");
        state.kpis.forEach((k, i) => {
            lines.push(`  ${i + 1}. ${k.name} — formula: ${k.formula} (${k.agg})${k.filter_col ? ` filtered by ${k.filter_col}` : ""}`);
        });
    }
    if (state.charts.length > 0) {
        lines.push("");
        lines.push("Charts:");
        state.charts.forEach((c, i) => {
            const meta = [c.x && `x=${c.x}`, c.y && `y=${c.y}`, c.split && `split=${c.split}`, c.limit && `top ${c.limit}`].filter(Boolean).join(", ");
            lines.push(`  ${i + 1}. ${c.type.toUpperCase()} — ${c.title}${meta ? ` (${meta})` : ""}`);
        });
    }
    if (state.filters.length > 0) {
        lines.push("");
        lines.push(`Global filter dimensions: ${state.filters.join(", ")}.`);
    }
    lines.push("");
    lines.push("Output: a complete Gallery prebuilt spec with widgets[] and filters[] ready to save.");
    return lines.join("\n");
}


export default function StructuredBuilderDrawer({ open, onClose, onInsertPrompt }) {
    const [state, setState] = useState({
        title: "",
        intent: "marketing",
        sources: [],
        description: "",
        kpis: [],
        charts: [],
        filters: [],
    });
    const [pickedTemplate, setPickedTemplate] = useState(null);
    // iter-119bk Builder smart-picker rail — fetches POST /sense-ai-builder/introspect
    // when the user selects an intent or applies a template, then renders ranked
    // chart proposals as one-click chips that append to state.charts. NO hardcoded
    // model_slug → uses the intent → model mapping from INTENT_MODEL_MAP.
    const [picker, setPicker] = useState({ loading: false, error: null, proposals: [], model: null });
    // P4 + P8 — the manual chart-type picker is driven SOLELY by the registry.
    // No hardcoded fallback list lives here (it would be a second source that
    // could drift from the registry / advertise an unavailable type). On a
    // failed/empty fetch the dropdown simply shows no options — the registry is
    // the single authority for what the picker offers. `kpi` is excluded (KPIs
    // have their own section). The backend still enforces support at add time.
    const [chartTypeOptions, setChartTypeOptions] = useState([]);
    useEffect(() => {
        let cancelled = false;
        api.get("/sense-ai-builder/available-chart-types")
            .then((r) => {
                if (cancelled) return;
                const types = (r.data?.chart_types || []).filter((t) => t !== "kpi");
                setChartTypeOptions(types);
            })
            .catch(() => { if (!cancelled) setChartTypeOptions([]); });
        return () => { cancelled = true; };
    }, []);

    const INTENT_TO_MODEL = {
        marketing: "marketing", sales: "sales", finance: "finance",
        ops: "operations", operations: "operations", product: "product",
        customer: "customer", customer_success: "customer",
    };

    useEffect(() => {
        const modelSlug = INTENT_TO_MODEL[state.intent] || null;
        if (!modelSlug) { setPicker({ loading: false, error: null, proposals: [], model: null }); return; }
        let cancelled = false;
        setPicker((p) => ({ ...p, loading: true, error: null, model: modelSlug }));
        api.post("/sense-ai-builder/introspect", { model_slug: modelSlug })
            .then((r) => {
                if (cancelled) return;
                const d = r.data || {};
                setPicker({
                    loading: false,
                    error: null,
                    proposals: d.proposals || [],
                    model: modelSlug,
                });
            })
            .catch((e) => {
                if (cancelled) return;
                setPicker({
                    loading: false,
                    error: e?.response?.data?.detail || "Could not load suggestions",
                    proposals: [],
                    model: modelSlug,
                });
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.intent]);

    const acceptProposal = (proposal) => {
        // Map picker's chart_type to the drawer's chart row shape. The
        // backend already returns widget_config with x/y/group_by filled
        // — we just convert it into the drawer's `{type, title, x, y}`.
        const chart = {
            type: proposal.chart_type,
            title: proposal.description || `${proposal.chart_type}: ${proposal.x || ""} × ${proposal.y || ""}`,
            x: proposal.x || "",
            y: proposal.y || "",
        };
        if (proposal.group_by) chart.group_by = proposal.group_by;
        setState((s) => ({ ...s, charts: [...s.charts, chart] }));
        toast.success(`Added ${proposal.chart_type} chart`, {
            description: `${proposal.x || "?"} × ${proposal.y || "?"}  ·  ${Math.round(proposal.confidence * 100)}% confidence`,
        });
    };

    const applyTemplate = (tpl) => {
        setPickedTemplate(tpl.id);
        setState({
            title: tpl.title,
            intent: tpl.intent,
            sources: [...tpl.sources],
            description: tpl.description,
            kpis: tpl.kpis.map((k) => ({ ...k })),
            charts: tpl.charts.map((c) => ({ ...c })),
            filters: [...tpl.filters],
        });
        toast.success(`Loaded "${tpl.title}" template — tweak as needed.`);
    };

    const addKpi    = () => setState((s) => ({ ...s, kpis: [...s.kpis, { name: "", formula: "", agg: "sum", filter_col: "" }] }));
    const removeKpi = (i) => setState((s) => ({ ...s, kpis: s.kpis.filter((_, idx) => idx !== i) }));
    const setKpi    = (i, patch) => setState((s) => ({ ...s, kpis: s.kpis.map((k, idx) => idx === i ? { ...k, ...patch } : k) }));

    const addChart    = () => setState((s) => ({ ...s, charts: [...s.charts, { type: "bar", title: "", x: "", y: "" }] }));
    const removeChart = (i) => setState((s) => ({ ...s, charts: s.charts.filter((_, idx) => idx !== i) }));
    const setChart    = (i, patch) => setState((s) => ({ ...s, charts: s.charts.map((c, idx) => idx === i ? { ...c, ...patch } : c) }));

    const addFilter   = () => setState((s) => ({ ...s, filters: [...s.filters, ""] }));
    const removeFilter = (i) => setState((s) => ({ ...s, filters: s.filters.filter((_, idx) => idx !== i) }));
    const setFilter   = (i, v) => setState((s) => ({ ...s, filters: s.filters.map((f, idx) => idx === i ? v : f) }));

    const prompt = buildPrompt(state);

    const onSend = () => {
        if (onInsertPrompt) onInsertPrompt(prompt);
        onClose();
    };

    const onCopy = async () => {
        try {
            await navigator.clipboard.writeText(prompt);
            toast.success("Prompt copied to clipboard");
        } catch {
            toast.error("Could not copy");
        }
    };

    return (
        <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
            <SheetContent className="w-[90vw] sm:w-[680px] sm:max-w-[680px] overflow-y-auto" data-testid="structured-builder">
                <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                        <Wand2 className="h-4 w-4 text-primary" />
                        Structured Template Builder
                        <Badge variant="outline" className="font-mono text-[10px]">SA</Badge>
                    </SheetTitle>
                    <SheetDescription>
                        Compose a dashboard template step-by-step. Generates a clean prompt
                        the Builder LLM converts into a Gallery prebuilt.
                    </SheetDescription>
                </SheetHeader>

                {/* Quick-start templates */}
                <section className="mt-5 space-y-2" data-testid="builder-templates">
                    <div className="eyebrow">Quick-start templates</div>
                    <div className="grid grid-cols-2 gap-2">
                        {PROMPT_TEMPLATES.map((tpl) => (
                            <button key={tpl.id} onClick={() => applyTemplate(tpl)}
                                    className={`text-left p-3 border rounded-sm transition-colors ${
                                        pickedTemplate === tpl.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/40 hover:bg-accent/30"
                                    }`}
                                    data-testid={`builder-template-${tpl.id}`}>
                                <div className="flex items-center justify-between mb-1">
                                    <div className="text-sm font-medium">{tpl.title}</div>
                                    <Badge variant="outline" className="font-mono text-[9px]">{tpl.intent}</Badge>
                                </div>
                                <div className="text-[11px] text-muted-foreground line-clamp-2">{tpl.description}</div>
                                <div className="flex gap-1 mt-1.5 flex-wrap">
                                    {tpl.sources.slice(0, 4).map((s) => (
                                        <span key={s} className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-muted/40 text-muted-foreground">{s}</span>
                                    ))}
                                </div>
                            </button>
                        ))}
                    </div>
                </section>

                {/* Basics */}
                <section className="mt-5 space-y-3" data-testid="builder-basics">
                    <div className="eyebrow">Basics</div>
                    <div className="space-y-2">
                        <Label className="text-xs">Title</Label>
                        <Input value={state.title} onChange={(e) => setState((s) => ({ ...s, title: e.target.value }))}
                               placeholder="Marketing Performance Q4" className="rounded-sm" data-testid="builder-title" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Intent</Label>
                            <Select value={state.intent} onValueChange={(v) => setState((s) => ({ ...s, intent: v }))}>
                                <SelectTrigger className="rounded-sm" data-testid="builder-intent"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {INTENTS.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Sources (multi)</Label>
                            <div className="flex flex-wrap gap-1 p-1.5 rounded-sm border border-border min-h-[36px]">
                                {state.sources.length === 0 && <span className="text-[10px] text-muted-foreground self-center">Pick one or more</span>}
                                {state.sources.map((src) => (
                                    <Badge key={src} onClick={() => setState((s) => ({ ...s, sources: s.sources.filter((x) => x !== src) }))}
                                           className="cursor-pointer">
                                        {src} ×
                                    </Badge>
                                ))}
                            </div>
                            <Select value="" onValueChange={(v) => v && setState((s) => s.sources.includes(v) ? s : ({ ...s, sources: [...s.sources, v] }))}>
                                <SelectTrigger className="rounded-sm mt-1 h-7 text-xs" data-testid="builder-source"><SelectValue placeholder="+ Add source" /></SelectTrigger>
                                <SelectContent>
                                    {SOURCES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">Description (optional)</Label>
                        <Textarea value={state.description} onChange={(e) => setState((s) => ({ ...s, description: e.target.value }))}
                                  rows={2} className="rounded-sm" data-testid="builder-desc"
                                  placeholder="What's this dashboard for?" />
                    </div>
                </section>

                {/* KPIs */}
                <section className="mt-5 space-y-2" data-testid="builder-kpis-section">
                    <div className="flex items-center justify-between">
                        <div className="eyebrow flex items-center gap-1.5"><Hash className="h-3 w-3" /> KPI Tiles</div>
                        <Button size="sm" variant="outline" onClick={addKpi} className="rounded-sm h-7 text-xs" data-testid="builder-add-kpi">
                            <Plus className="h-3 w-3 mr-1" /> Add KPI
                        </Button>
                    </div>
                    {state.kpis.length === 0 && (
                        <div className="text-[11px] text-muted-foreground border border-dashed border-border rounded-sm p-3 text-center">
                            No KPIs yet. Click <b>Add KPI</b> or pick a template above.
                        </div>
                    )}
                    {state.kpis.map((k, i) => (
                        <div key={i} className="grid grid-cols-[1fr_1fr_80px_1fr_auto] gap-2 items-center" data-testid={`builder-kpi-row-${i}`}>
                            <Input value={k.name} onChange={(e) => setKpi(i, { name: e.target.value })}
                                   placeholder="KPI name" className="h-8 rounded-sm text-xs" />
                            <Input value={k.formula} onChange={(e) => setKpi(i, { formula: e.target.value })}
                                   placeholder="SUM(spend)" className="h-8 rounded-sm text-xs font-mono" />
                            <Select value={k.agg} onValueChange={(v) => setKpi(i, { agg: v })}>
                                <SelectTrigger className="h-8 rounded-sm text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {["sum", "avg", "min", "max", "count", "ratio"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <Input value={k.filter_col} onChange={(e) => setKpi(i, { filter_col: e.target.value })}
                                   placeholder="filter by" className="h-8 rounded-sm text-xs" />
                            <Button size="icon" variant="ghost" onClick={() => removeKpi(i)} className="h-8 w-8 rounded-sm" data-testid={`builder-kpi-rm-${i}`}>
                                <Trash2 className="h-3 w-3" />
                            </Button>
                        </div>
                    ))}
                </section>

                {/* iter-119bk — AI Suggested Charts (Phase 7 smart picker) */}
                <section className="mt-5 space-y-2" data-testid="builder-picker-rail">
                    <div className="flex items-center justify-between">
                        <div className="eyebrow flex items-center gap-1.5">
                            <Lightbulb className="h-3 w-3 text-primary" />
                            AI suggested charts
                            {picker.model && (
                                <span className="font-mono text-[9px] text-muted-foreground">
                                    model.{picker.model}
                                </span>
                            )}
                        </div>
                        {picker.loading && (
                            <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                                <Loader2 className="h-3 w-3 animate-spin" /> Introspecting…
                            </span>
                        )}
                    </div>
                    {!picker.model && !picker.loading && (
                        <div className="text-[11px] text-muted-foreground border border-dashed border-border rounded-sm p-3 text-center">
                            Pick an intent above to see chart suggestions tuned to your data shape.
                        </div>
                    )}
                    {picker.error && (
                        <div className="text-[11px] text-amber-600 border border-amber-500/30 bg-amber-500/5 rounded-sm p-2">
                            {picker.error}
                        </div>
                    )}
                    {!picker.loading && picker.proposals.length > 0 && (
                        <>
                            <p className="text-[10px] text-muted-foreground leading-relaxed">
                                Tap any chip to append it to your <b>Charts</b> list below — the backend's
                                semantic picker ranked these against your <code className="text-[10px]">{picker.model}</code> data shape.
                                Higher confidence = stronger signal in the data.
                            </p>
                            <div className="flex flex-wrap gap-1.5" data-testid="builder-picker-chips">
                                {picker.proposals.slice(0, 12).map((p, i) => {
                                    const intensity = Math.round(p.confidence * 100);
                                    const tone = p.confidence >= 0.85
                                        ? "border-primary text-primary hover:bg-primary/10"
                                        : p.confidence >= 0.7
                                            ? "border-primary/60 text-primary/90 hover:bg-primary/5"
                                            : "border-border text-muted-foreground hover:bg-accent/30";
                                    return (
                                        <button key={`${p.rule_id}-${i}`} type="button"
                                            onClick={() => acceptProposal(p)}
                                            title={`${p.description} · x=${p.x || "?"} · y=${p.y || "?"} ${p.group_by ? "· group_by=" + p.group_by : ""}`}
                                            className={`inline-flex items-center gap-1.5 border rounded-sm px-2 py-1 text-[10px] font-mono transition-colors ${tone}`}
                                            data-testid={`builder-picker-chip-${p.rule_id}`}>
                                            <BarChart3 className="h-2.5 w-2.5" />
                                            <span className="font-semibold">{p.chart_type}</span>
                                            <span className="opacity-70">{p.x || "?"} × {p.y || "?"}</span>
                                            <span className="bg-muted/60 px-1 rounded-sm text-[9px]">{intensity}%</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    )}
                    {!picker.loading && picker.model && picker.proposals.length === 0 && !picker.error && (
                        <div className="text-[11px] text-muted-foreground italic">
                            No proposals — model.{picker.model} doesn't have enough sample rows yet. Ingest data first.
                        </div>
                    )}
                </section>

                {/* Charts */}
                <section className="mt-5 space-y-2" data-testid="builder-charts-section">
                    <div className="flex items-center justify-between">
                        <div className="eyebrow flex items-center gap-1.5"><BarChart3 className="h-3 w-3" /> Charts</div>
                        <Button size="sm" variant="outline" onClick={addChart} className="rounded-sm h-7 text-xs" data-testid="builder-add-chart">
                            <Plus className="h-3 w-3 mr-1" /> Add chart
                        </Button>
                    </div>
                    {state.charts.length === 0 && (
                        <div className="text-[11px] text-muted-foreground border border-dashed border-border rounded-sm p-3 text-center">
                            No charts yet.
                        </div>
                    )}
                    {state.charts.map((c, i) => (
                        <div key={i} className="grid grid-cols-[100px_1fr_120px_120px_auto] gap-2 items-center" data-testid={`builder-chart-row-${i}`}>
                            <Select value={c.type} onValueChange={(v) => setChart(i, { type: v })}>
                                <SelectTrigger className="h-8 rounded-sm text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {chartTypeOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <Input value={c.title} onChange={(e) => setChart(i, { title: e.target.value })}
                                   placeholder="Chart title" className="h-8 rounded-sm text-xs" />
                            <Input value={c.x || ""} onChange={(e) => setChart(i, { x: e.target.value })}
                                   placeholder="x-axis" className="h-8 rounded-sm text-xs font-mono" />
                            <Input value={c.y || ""} onChange={(e) => setChart(i, { y: e.target.value })}
                                   placeholder="y-axis" className="h-8 rounded-sm text-xs font-mono" />
                            <Button size="icon" variant="ghost" onClick={() => removeChart(i)} className="h-8 w-8 rounded-sm" data-testid={`builder-chart-rm-${i}`}>
                                <Trash2 className="h-3 w-3" />
                            </Button>
                        </div>
                    ))}
                </section>

                {/* Filters */}
                <section className="mt-5 space-y-2" data-testid="builder-filters-section">
                    <div className="flex items-center justify-between">
                        <div className="eyebrow flex items-center gap-1.5"><Filter className="h-3 w-3" /> Filter dimensions</div>
                        <Button size="sm" variant="outline" onClick={addFilter} className="rounded-sm h-7 text-xs" data-testid="builder-add-filter">
                            <Plus className="h-3 w-3 mr-1" /> Add filter
                        </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {state.filters.map((f, i) => (
                            <div key={i} className="flex items-center gap-1" data-testid={`builder-filter-${i}`}>
                                <Input value={f} onChange={(e) => setFilter(i, e.target.value)}
                                       placeholder="column" className="h-7 rounded-sm text-xs font-mono w-32" />
                                <Button size="icon" variant="ghost" onClick={() => removeFilter(i)} className="h-7 w-7 rounded-sm">
                                    <Trash2 className="h-3 w-3" />
                                </Button>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Generated prompt preview */}
                <section className="mt-6 space-y-2" data-testid="builder-prompt-preview">
                    <div className="eyebrow flex items-center gap-1.5"><Eye className="h-3 w-3" /> Generated prompt</div>
                    <Textarea value={prompt} readOnly rows={8} className="rounded-sm font-mono text-xs bg-muted/30" data-testid="builder-prompt-textarea" />
                </section>

                {/* Actions */}
                <div className="sticky bottom-0 bg-background pt-3 pb-2 mt-4 border-t border-border flex justify-between gap-2">
                    <Button variant="ghost" onClick={onCopy} className="rounded-sm" data-testid="builder-copy">
                        <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                    </Button>
                    <Button onClick={onSend} className="rounded-sm" data-testid="builder-send">
                        <Sparkles className="h-3.5 w-3.5 mr-1" /> Send to Builder
                        <ChevronRight className="h-3 w-3 ml-1" />
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
}
